/**
 * SavedSearches — dashboard listing a signed-in user's stashed Quick
 * Check searches with a live "your matches shifted +N since" delta
 * pulled from `/api/saved-searches/{id}/refresh`. The delta is the
 * retention hook — returning families see at a glance how the
 * catalog has grown into their profile.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Bookmark, RefreshCcw, Trash2, TrendingUp, TrendingDown } from "lucide-react";

export default function SavedSearches() {
  const { user, login, loading: authLoading } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deltas, setDeltas] = useState({});   // { [id]: refresh response }
  const [refreshing, setRefreshing] = useState(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    api
      .get("/saved-searches")
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [user]);

  const doRefresh = async (id) => {
    setRefreshing(id);
    try {
      const { data } = await api.get(`/saved-searches/${id}/refresh`);
      setDeltas((d) => ({ ...d, [id]: data }));
    } catch {
      toast.error("Could not refresh — please try again.");
    } finally {
      setRefreshing(null);
    }
  };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this saved search?")) return;
    try {
      await api.delete(`/saved-searches/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  if (authLoading) return null;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 text-center">
        <Bookmark className="h-10 w-10 mx-auto text-stone-400" />
        <h1
          data-testid="saved-searches-heading"
          className="mt-4 font-editorial text-3xl"
        >
          Your saved searches
        </h1>
        <p className="mt-3 text-stone-600 max-w-md mx-auto">
          Sign in to see the Quick Check searches you've stashed and how
          your chances have shifted since you saved them.
        </p>
        <button
          type="button"
          onClick={login}
          data-testid="saved-searches-sign-in"
          className="mt-6 px-5 py-2.5 rounded-md bg-[#4C1D95] text-white hover:bg-[#3b1479] text-sm font-underground-sc tracking-wider"
        >
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
      <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
        <Bookmark className="h-3 w-3" /> Your saved searches
      </div>
      <h1
        data-testid="saved-searches-heading"
        className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight"
      >
        How have your chances shifted?
      </h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        Each search here re-runs against the live catalog when you tap
        Refresh — the delta shows how many courses have moved in or out
        of your bucket since you saved.
      </p>

      {loading && (
        <div className="mt-8 p-4 text-stone-500 text-sm">Loading…</div>
      )}

      {!loading && !rows.length && (
        <div
          data-testid="saved-searches-empty"
          className="mt-8 rounded-md border border-dashed border-stone-300 bg-stone-50/60 p-6 text-sm text-stone-600"
        >
          You haven't saved any searches yet. Run a{" "}
          <Link to="/admission" className="text-[#4C1D95] underline">
            Quick Check
          </Link>{" "}
          and tap "Save this search" to stash the result.
        </div>
      )}

      <div className="mt-6 space-y-3">
        {rows.map((row) => (
          <SavedRow
            key={row.id}
            row={row}
            delta={deltas[row.id]}
            onRefresh={() => doRefresh(row.id)}
            onDelete={() => doDelete(row.id)}
            refreshing={refreshing === row.id}
          />
        ))}
      </div>
    </div>
  );
}

function SavedRow({ row, delta, onRefresh, onDelete, refreshing }) {
  const dTotal = delta?.delta?.total_matches;
  return (
    <div
      data-testid={`saved-search-${row.id}`}
      className="rounded-md border border-stone-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
    >
      <div className="min-w-0 flex-1">
        <div className="font-editorial text-lg text-[#002147]">
          {row.name}
        </div>
        <div className="mt-1 text-[11px] font-mono text-stone-500">
          {row.kind === "quick_check" ? "Quick Check" : "Advanced Match"}
          {" · "}
          Saved {new Date(row.created_at).toLocaleDateString()}
          {" · "}
          <span className="text-stone-600">
            {row.baseline_snapshot?.total_matches ?? 0} baseline match
            {row.baseline_snapshot?.total_matches === 1 ? "" : "es"}
          </span>
        </div>
        {delta && (
          <div
            className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider ${
              dTotal > 0
                ? "text-emerald-700"
                : dTotal < 0
                ? "text-rose-700"
                : "text-stone-500"
            }`}
            data-testid={`saved-search-${row.id}-delta`}
          >
            {dTotal > 0 && <TrendingUp className="h-3 w-3" />}
            {dTotal < 0 && <TrendingDown className="h-3 w-3" />}
            {dTotal > 0
              ? `+${dTotal} matches`
              : dTotal < 0
              ? `${dTotal} matches`
              : "No change"}
            {" · now "}
            {delta.current?.total_matches ?? 0}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          data-testid={`saved-search-${row.id}-refresh`}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#4C1D95] text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white px-3 py-1.5 text-[11px] font-underground-sc tracking-wider transition-colors disabled:opacity-50"
        >
          <RefreshCcw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid={`saved-search-${row.id}-delete`}
          className="inline-flex items-center gap-1 text-stone-400 hover:text-rose-600 p-1.5"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
